import React, { useContext, useEffect, useState } from 'react'
import TextField from '@material-ui/core/TextField'
import { FormattedMessageModel } from './types/fieldsModel'
import { FieldRenderProps } from 'react-final-form'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { makeStyles, Theme } from '@material-ui/core/styles'
import styles from '../../styles'
import { useIntl } from 'react-intl'
import { ResponsiveContext } from '../../utils/context/responsiveContext'
import Loader from '../loader/Loader'
import { Autocomplete } from '@material-ui/lab'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => (
		{
			...styles(theme),
			textFieldStyle: {
				width: '100%',
				padding: '8px 8px 8px 0px',
				boxSizing: 'border-box'
			},
			textFieldLargeScreenStyle: {
				'& .MuiInput-formControl': {
					'& .MuiInput-input': {
						fontSize: 20,
						fontWeight: 500,
						color: theme.palette.blue.main
					}
				}
			},
			textFieldSmallScreenStyle: {
				'& .MuiInput-formControl': {
					'& .MuiInput-input': {
						fontSize: 14,
						fontWeight: 500,
						color: theme.palette.blue.main
					}
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: 10
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			errorBorder: {
				border: '1px solid #B92D00'
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label?: string,
	placeholder?: FormattedMessageModel,
	multiline?: boolean,
	maxLength?: number,
	fetchOptions: (param: any) => Promise<unknown>
}

type AutocompleteFieldProps =
	FieldRenderProps<string>
	& AcceptingProps

const FormAutocompleteField: React.FC<AutocompleteFieldProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		id,
		label,
		placeholder,
		multiline,
		maxLength,
		disabled,
		fetchOptions
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })
	const intl = useIntl()
	const [open, setOpen] = useState(false)
	const [options, setOptions] = useState<string[]>([])
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!open) {
			setOptions([])
		}
	}, [open])

	const [timer, setTimer] = useState<any>();

	const fetchAndLoad = (value: string) => {
		setLoading(true)
		const newTimer = setTimeout(() => {
			fetchOptions(value)
				.then((response: any) => {
					setOptions(response)
					setOpen(true)
				})
				.finally(() => setLoading(false))
		}, 750)
		clearTimeout(timer);
		setTimer(newTimer);
	}

	const labelClass: ClassValue = classNames(
		classes.text5,
		classes.colorBlue,
		{
			[classes.errorColor]: touched && error
		}
	)

	const textFieldClass: ClassValue = classNames(
		classes.textFieldStyle,
		{
			[classes.textFieldLargeScreenStyle]: isLargeScreen,
			[classes.textFieldSmallScreenStyle]: !isLargeScreen,
			[classes.errorBorder]: touched && error
		}
	)

	return (
		<>
		<span
			className={labelClass}
		>
				{label}
			</span>
			<Autocomplete
				open={open}
				onOpen={() => {
					if (options.length) {
						setOpen(true)
					}
				}}
				onClose={() => {
					setOpen(false)
				}}
				getOptionSelected={(option, value) => option === value}
				getOptionLabel={(option) => option}
				options={loading ? [] : options}
				freeSolo
				loading={loading}
				defaultValue={input.value}
				onInputChange={(event, value) => {
					value.length >= 3 ? fetchAndLoad(value) : setOptions([])
				}}
				onSelect={(event) => {
					//@ts-ignore
					input.onChange(event.target.value)
				}}
				renderInput={(params) => (
					<>
						<TextField
							id={id}
							disabled={disabled}
							placeholder={placeholder ? intl.formatMessage({
								id: placeholder.id,
								defaultMessage: placeholder.defaultMessage,
								description: placeholder.description
							}, placeholder.values) : undefined}
							className={textFieldClass}
							multiline={multiline}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<React.Fragment>
										{loading ? <Loader size={20} /> : null}
										{params.InputProps.endAdornment}
									</React.Fragment>
								),
								maxLength: maxLength,
								disableUnderline: true
							}}
							{...params}
							{...input}
						/>

						{
							touched &&
							((error && <span className={classes.errorStyle}>{error}</span>))
						}
					</>
				)}
			/>
		</>
	)
}

export default FormAutocompleteField